// API service for fetching detailed charger data from webapp
// This matches the webapp API that returns rich connector information

const WEBAPP_API_BASE = 'https://31evz9fs4m.execute-api.ap-south-1.amazonaws.com';

/**
 * Fetch detailed charger data from webapp API
 * This API returns the same rich data that webapp shows including proper connector types and power
 */
export const fetchChargerDetailFromWebapp = async (lat, lng) => {
  try {
    
    const url = `${WEBAPP_API_BASE}/dev/v2/location/get-area-location-detail?lat=${lat}&lng=${lng}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // Add any required headers for webapp API
      },
    });

    if (!response.ok) {
      throw new Error(`Webapp API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Merge CMS charger data with webapp detailed data
 * Combines the basic CMS data with rich connector information from webapp
 */
export const mergeChargerWithWebappData = (cmsCharger, webappData) => {
  try {
    
    if (!webappData || !webappData.data) {
      return cmsCharger;
    }

    // Extract webapp charger data - might be in different structures
    const webappCharger = webappData.data.charger || 
                          webappData.data.chargers?.[0] || 
                          webappData.data.location || 
                          webappData.data;

    if (!webappCharger) {
      return cmsCharger;
    }

    // Merge the data, prioritizing webapp connector information
    const mergedCharger = {
      ...cmsCharger,
      // Keep CMS basic info but enhance with webapp data
      webappData: webappCharger,
      
      // Override connectors with webapp data if available
      connectors: webappCharger.connectors || 
                  webappCharger.devices?.flatMap(d => d.stations || []) || 
                  cmsCharger.connectors,
      
      // Add any additional webapp fields
      webappName: webappCharger.name,
      webappAddress: webappCharger.address,
      webappPower: webappCharger.power,
      webappOperator: webappCharger.operator,
      
      // Merge devices if webapp has them
      devices: webappCharger.devices || cmsCharger.devices,
      
      // Add webapp-specific fields
      webappResponse: webappData
    };

    return mergedCharger;
    
  } catch (error) {
    return cmsCharger; // Fallback to original CMS data
  }
};

/**
 * Enhanced charger selection with webapp API integration
 * Fetches detailed data and merges it with CMS data
 */
export const enhanceChargerWithWebappData = async (charger) => {
  try {
    // Extract coordinates from various possible sources
    const lat = charger.latitude || 
                charger.locationLatitude || 
                charger.lat || 
                charger.location?.lat;
                
    const lng = charger.longitude || 
                charger.locationLongitude || 
                charger.lng || 
                charger.location?.lng;

    if (!lat || !lng) {
      return charger;
    }

    // Fetch detailed data from webapp API
    const webappData = await fetchChargerDetailFromWebapp(lat, lng);
    
    // Merge with CMS data
    const enhancedCharger = mergeChargerWithWebappData(charger, webappData);
    
    return enhancedCharger;
    
  } catch (error) {
    // Return original charger data if webapp API fails
    return charger;
  }
};